import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, RotateCcw, Phone, Mail } from 'lucide-react';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import { CONTACT } from '../content/site';
import usePageMeta from '../hooks/usePageMeta';

const H2 = 'font-display text-3xl font-bold uppercase tracking-tight text-white';
const LABEL = 'font-mono text-[10px] uppercase tracking-[0.25em] text-accentText';
const BODY = 'text-sm leading-relaxed text-dim';
const CARD = 'border border-line bg-base p-7 sm:p-8';

const NOT_GUARANTEED = [
  'That a child or pet will not be able to turn the knob.',
  'That the knob will function as described above if it is not installed properly.',
  'That the knob will prevent a fire or will prevent an unlit burner from being turned on by mistake.',
];

const WARRANTY_EXCLUSIONS = [
  'Has been subject to misuse, mishandling, or accident after the delivery point; or',
  'Has been tampered with, altered, or repaired by someone other than supplier or a supplier-authorized repair facility; or',
  'Installed incorrectly by customer, customer subsidiary or an unauthorized installer; or',
  'Has been damaged due to an oven operated with an open door; or',
  'Has been damaged from an uncovered/partially covered surface burner on stoves with rear mounted knobs that are left in on position; or',
  'Has been damaged due to the application of harsh chemical cleaners.',
];

const RETURN_REQUIREMENTS = [
  'The return shipping date of your return must be within the 30-day period described above.',
  'You have not used or installed any of the RangeSafe Safety Knobs you purchased.',
  'The returned RangeSafe Safety Knobs have not been damaged.',
  'You return all of the RangeSafe Safety Knobs™ and inserts that were included with your order (you do not need to return the instructions).',
  'Return shipping costs are covered by the returner.',
];

const SHIPPING_STEPS = [
  'Put your RangeSafe Safety product in the RangeSafe box in which you received it and pack that box in an outer box using the bubble wrap that came with your order. Put inside the package a piece of paper with your name, address, telephone number and email address.',
  'Mail or ship the RangeSafe Safety Knobs at your expense to the address on the RMA label we provided to you. You should consider using a trackable shipping service such as the United States Postal Service, which we use.',
];

function RomanList({ items, testid }) {
  return (
    <ol className="space-y-3" data-testid={testid}>
      {items.map((t, i) => (
        <li key={t} className="flex gap-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accentText">{String(i + 1).padStart(2, '0')}</span>
          <span className={BODY}>{t}</span>
        </li>
      ))}
    </ol>
  );
}

function LegalBlock({ label, text, testid }) {
  return (
    <div className={CARD} data-testid={testid}>
      <p className={LABEL}>{label}</p>
      <p className={`mt-4 ${BODY}`}>{text}</p>
    </div>
  );
}

export default function Warranty() {
  usePageMeta(
    'Warranty & 30-Day Money Back Policy | RangeSafe Commercial Solutions',
    'The published RangeSafe warranty: two-year coverage against defects in materials and workmanship, warranty exclusions, limitations and the 30-day money-back return policy.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Support"
          lines={['Warranty &', 'money back policy']}
          lead="The published RangeSafe warranty and return policy: every RangeSafe Knob™ is warranted to be free from defects in materials and workmanship for two (2) years from the date of sale and retail orders are covered by a 30-day money-back policy."
        />
      </header>

      <div className="mx-auto max-w-shell space-y-20 px-5 pb-24 sm:px-8">
        <section data-testid="warranty-terms">
          <h2 className={H2}>
            <ShieldCheck className="mr-3 inline h-6 w-6 text-accentText" aria-hidden="true" />
            Warranty, exclusions, limitations &amp; disclaimers
          </h2>
          <Reveal>
            <p className={`mt-8 max-w-3xl ${BODY}`}>
              The RangeSafe Knob™ is designed to make it more difficult for a burner on a stove or range to be turned on by accident or by a small child or by a household pet. In other words, the RangeSafe Knob™ reduces the potential for a stove or range to be turned on by mistake or by someone who is not competent to use the stove.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Reveal className={CARD}>
              <div data-testid="warranty-not-guaranteed">
                <p className={LABEL}>RangeSafe LLC does not guarantee</p>
                <div className="mt-4">
                  <RomanList items={NOT_GUARANTEED} testid="warranty-not-guaranteed-list" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.06} className={CARD}>
              <div data-testid="warranty-sole">
                <p className={LABEL}>Sole warranty</p>
                <p className={`mt-4 ${BODY}`}>
                  RangeSafe LLC sole warranty to the purchaser of a RangeSafe Knob™ is that the knob will be free from defects in materials and workmanship for a period of two (2) years from the date of sale.
                </p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-accentText">The warranty does not extend to a product that</p>
                <div className="mt-4">
                  <RomanList items={WARRANTY_EXCLUSIONS} testid="warranty-exclusions-list" />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <LegalBlock
                label="RangeSafe LLC sole obligation"
                text="RangeSafe LLC sole obligation for a breach of the foregoing warranty is to provide a replacement RangeSafe Knob™."
                testid="warranty-obligation"
              />
            </Reveal>
            <Reveal delay={0.06}>
              <LegalBlock
                label="Exclusion of other warranties"
                text="RangeSafe LLC makes no other warranties of any kind, either express or implied, including, but not limited to warranties of merchantability, fitness for a particular purpose, or of non-infringement of a third party’s rights."
                testid="warranty-exclusion-others"
              />
            </Reveal>
            <Reveal>
              <LegalBlock
                label="Limitation of liability"
                text="In no event will RangeSafe LLC be liable for any direct, indirect, punitive, incidental, special, or consequential damages, to property or life, whatsoever arising out of, or connected with, the use or misuse of a RangeSafe Knob™."
                testid="warranty-liability"
              />
            </Reveal>
            <Reveal delay={0.06}>
              <LegalBlock
                label="Unenforceability"
                text="If any part of the foregoing warranty, exclusions, limitations and disclaimers set forth above is determined to be invalid, void, unenforceable or illegal, then the invalid or unenforceable provision will be deemed superseded by a valid, enforceable provision that most closely matches the intent of the original provision if possible, or if that is not possible the provision will be deleted, but all the other provisions will remain in full force and effect."
                testid="warranty-unenforceability"
              />
            </Reveal>
          </div>
        </section>

        <section data-testid="warranty-moneyback">
          <h2 className={H2}>
            <RotateCcw className="mr-3 inline h-6 w-6 text-accentText" aria-hidden="true" />
            30 day money back policy
          </h2>
          <Reveal>
            <p className={`mt-8 max-w-3xl ${BODY}`}>
              Your RangeSafe Safety Knob™* order is covered by our 30 day money back policy. The 30-day period starts upon the delivery date of your order. If you are not satisfied with your RangeSafe Safety Knobs™ upon opening, you may return them within 30 days of the delivery date for a full refund of your purchase price and any tax collected. However, to receive a refund, you must follow these important requirements as well as the shipping instructions below:
            </p>
          </Reveal>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Reveal className={CARD}>
              <div data-testid="warranty-return-requirements">
                <p className={LABEL}>Refund requirements</p>
                <div className="mt-4">
                  <RomanList items={RETURN_REQUIREMENTS} testid="warranty-requirements-list" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.06} className={CARD}>
              <div data-testid="warranty-return-process">
                <p className={LABEL}>Return process</p>
                <p className={`mt-4 ${BODY}`}>
                  Call customer service at <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, '')}`} className="text-white underline decoration-line underline-offset-4 transition-colors hover:decoration-accent" data-testid="warranty-rma-phone">{CONTACT.phone}</a> and ask for a Return Merchandise Authorization (RMA) number.
                </p>
                <p className={`mt-3 ${BODY}`}>
                  Or request an RMA number by email to <a href={`mailto:${CONTACT.generalEmail}`} className="text-white underline decoration-line underline-offset-4 transition-colors hover:decoration-accent" data-testid="warranty-rma-email">{CONTACT.generalEmail}</a>. Make sure to provide in your email your name, address, phone number and email address.
                </p>
                <p className={`mt-3 ${BODY}`}>
                  Please do not send your RangeSafe Safety Knob(s) back to anyone except to the address printed on the return label provided to you.
                </p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-accentText">Shipping instructions</p>
                <div className="mt-4">
                  <RomanList items={SHIPPING_STEPS} testid="warranty-shipping-list" />
                </div>
              </div>
            </Reveal>
          </div>
          <Reveal>
            <div className="mt-6 space-y-4 max-w-3xl">
              <p className={BODY}>
                Once your return is received and inspected, we will send you an email to notify you that we have received it. We will also notify you if your refund request is rejected and the reason for the rejection.
              </p>
              <p className={BODY}>
                If approved, your refund will be processed and a credit will be applied to your credit card or original method of payment. It may take some time before this credit is officially posted to your credit card or other account by the account provider.
              </p>
              <p className={BODY}>
                As noted above, you will be responsible for paying the shipping costs for returning the RangeSafe product. Original shipping costs are non-refundable. If when we receive the returned product you have not paid for the return shipping, that cost will be deducted from your refund.
              </p>
              <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-mute">
                *RangeSafe Safety Knobs is a trademark of RangeSafe, LLC.
              </p>
            </div>
          </Reveal>
        </section>

        <section data-testid="warranty-support">
          <div className="border border-line bg-panel/60 p-8 sm:p-10">
            <p className={LABEL}>Warranty &amp; support questions</p>
            <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white">Talk to the RangeSafe team</h3>
            <p className={`mt-3 max-w-2xl ${BODY}`}>
              Questions about coverage, a warranty claim or a return — reach the team directly.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, '')}`} className="inline-flex items-center gap-2 border border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-accent" data-testid="warranty-support-phone">
                <Phone className="h-3.5 w-3.5 text-accentText" aria-hidden="true" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.generalEmail}`} className="inline-flex items-center gap-2 border border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-accent" data-testid="warranty-support-email">
                <Mail className="h-3.5 w-3.5 text-accentText" aria-hidden="true" /> {CONTACT.generalEmail}
              </a>
              <Link to="/resources" className="inline-flex items-center gap-2 border border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-accent" data-testid="warranty-back-resources">
                Back to resources <ArrowRight className="h-3.5 w-3.5 text-accentText" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
